import _ from "lodash";

/** internal */
import {
  SIZE,
  EVALUATION_METHOD,
  getUnitPlanFieldValue,
} from "modules/Services";

import {
  getLearningGoalsToFilter,
  updateDynamicLabelsForPlannerElements,
} from "Assessments/modules/Utils";

import {
  AI_TUTOR_INPUT_METHODS,
  AI_TUTOR_OUTPUT_METHODS,
  ASSESSMENT_TOOLS,
  STUDENT_COMMUNICATION_MODES,
  TUTOR_COMMUNICATION_MODES,
} from "@toddle-edu/common-utils";

// modules
export const getFieldOptionsLocale = ({ options, key, t }) => {
  const clonedOptions = _.cloneDeep(options);
  switch (key) {
    case "measureAssessing":
      _.map(clonedOptions, option => {
        if (option?.label) {
          option.label = t(
            _.get(
              _.find(ASSESSMENT_TOOLS, { type: option?.value }),
              "locale",
              ""
            )
          );
        }
        if (option?.options) {
          option.options = _.map(option.options, opt => {
            const { key } = opt || {};

            if (_.startsWith(key, "create_new")) {
              return {
                ...opt,
                label: t("common:create_new"),
              };
            }

            if (_.startsWith(key, "use_from_template")) {
              return {
                ...opt,
                label: t("common:select_a_with_label", {
                  label: t("common:template"),
                }),
              };
            }

            switch (key) {
              case "ASSESSMENT_GRADE_BOUNDARY": {
                return {
                  ...opt,
                  label: t("classRoom:grade_manually"),
                  subText: t("classRoom:grade_manually_subtext"),
                };
              }
              case "ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY": {
                return {
                  ...opt,
                  label: t("classRoom:grade_automatically"),
                  subText: t("classRoom:grade_automatically_subtext"),
                };
              }
              default:
                return opt;
            }
          });
        }
      });
      return clonedOptions;
    case "size":
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(_.find(SIZE, { type: option.value }), "locale", "")
        );
      });
      return clonedOptions;
    case "evaluationType":
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(_.find(EVALUATION_METHOD, { type: option.value }), "locale", "")
        );
      });
      return clonedOptions;
    case "inputMethod": {
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(
            _.find(AI_TUTOR_INPUT_METHODS, { id: option.id }),
            "localeLabel",
            ""
          )
        );
      });
      return clonedOptions;
    }
    case "outputMethod": {
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(
            _.find(AI_TUTOR_OUTPUT_METHODS, { id: option.id }),
            "localeLabel",
            ""
          )
        );
      });
      return clonedOptions;
    }
    case "studentCommunicationMode": {
      _.map(clonedOptions, option => {
        const currentOptionInfo = _.find(STUDENT_COMMUNICATION_MODES, {
          id: option.id,
        });

        option.label = t(_.get(currentOptionInfo, "localeLabel", ""));
        option.subText = t(_.get(currentOptionInfo, "subTextLocale", ""));
      });
      return clonedOptions;
    }
    case "tutorCommunicationMode": {
      _.map(clonedOptions, option => {
        const currentOptionInfo = _.find(TUTOR_COMMUNICATION_MODES, {
          id: option.id,
        });

        option.label = t(_.get(currentOptionInfo, "localeLabel", ""));
        option.subText = t(_.get(currentOptionInfo, "subTextLocale", ""));
      });
      return clonedOptions;
    }
    default:
      return clonedOptions;
  }
};

export const getAddLearningGoalsDropdownOptions = ({
  options,
  unitPlanTemplate,
  resourceFields,
  subjects,
  fieldList,
  plannerElementSets,
}) => {
  if (!_.isEmpty(plannerElementSets) && !_.isEmpty(fieldList)) {
    updateDynamicLabelsForPlannerElements({
      field_list: fieldList,
      plannerElementSets,
    });
  }

  const unitPlanField = _.find(resourceFields, { uid: "unitPlan" }) || {};
  const unitPlanResolvedMinimalTree = _.get(
    unitPlanField,
    "resolvedMinimalTree.unitPlan",
    {}
  );
  const unitPlanFields = _.get(unitPlanResolvedMinimalTree, "allFields", []);
  const unitType = _.get(
    _.find(unitPlanFields, { uid: "unitType" }),
    "value",
    ""
  );

  const optionsToRemove = getLearningGoalsToFilter({ subjects, unitType });
  const filteredOptions = _.filter(
    options,
    ({ key }) => !_.includes(optionsToRemove, key)
  );

  const optionsWithLocalizedLabel = _.map(filteredOptions, option => ({
    ...option,
    label: _.get(fieldList, `${option?.key}.config.label`, option?.label),
  }));

  if (!_.isEmpty(unitPlanTemplate)) {
    const unitPlanStageList = _.get(unitPlanTemplate, "stage_list", {});
    let isInquiryQuestionsV2PresentInUnitPlan = false;
    _.forEach(unitPlanStageList, ({ steps }) => {
      isInquiryQuestionsV2PresentInUnitPlan =
        isInquiryQuestionsV2PresentInUnitPlan || _.includes(steps, "IQV2");
    }) > -1;

    const fieldToRemove = !isInquiryQuestionsV2PresentInUnitPlan
      ? "inquiryQuestionsV2"
      : "inquiryQuestions";

    return _.filter(
      optionsWithLocalizedLabel,
      ({ key }) => key !== fieldToRemove
    );
  }

  return optionsWithLocalizedLabel;
};

export const getOptionsLocale = ({ options, key, t }) => {
  const clonedOptions = _.cloneDeep(options);
  switch (key) {
    case "measureAssessing":
      _.map(clonedOptions, option => {
        if (option?.label) {
          option.label = t(
            _.get(
              _.find(ASSESSMENT_TOOLS, { type: option?.value }),
              "locale",
              ""
            )
          );
        }
        if (option?.options) {
          option.options = _.map(option.options, opt => {
            const { key } = opt || {};

            if (_.startsWith(key, "create_new")) {
              return {
                ...opt,
                label: t("common:create_new"),
              };
            }

            if (_.startsWith(key, "use_from_template")) {
              return {
                ...opt,
                label: t("common:select_a_with_label", {
                  label: t("common:template"),
                }),
              };
            }

            switch (key) {
              case "ASSESSMENT_GRADE_BOUNDARY": {
                return {
                  ...opt,
                  label: t("classRoom:grade_manually"),
                  subText: t("classRoom:grade_manually_subtext"),
                };
              }
              case "ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY": {
                return {
                  ...opt,
                  label: t("classRoom:grade_automatically"),
                  subText: t("classRoom:grade_automatically_subtext"),
                };
              }
              default:
                return opt;
            }
          });
        }
      });
      return clonedOptions;
    case "size":
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(_.find(SIZE, { type: option.value }), "locale", "")
        );
      });
      return clonedOptions;
    case "evaluationType":
      _.map(clonedOptions, option => {
        option.label = t(
          _.get(_.find(EVALUATION_METHOD, { type: option.value }), "locale", "")
        );
      });
      return clonedOptions;
    default:
      return clonedOptions;
  }
};

export const getSubFields = ({ fieldKey, fieldObj, unitPlanFields }) => {
  const subFields = _.get(fieldObj, "subFields");

  if (
    _.get(fieldObj, "type", "") === "CheckListWithPopup" ||
    fieldKey === "inquiryQuestionsV2" ||
    _.isEmpty(subFields)
  ) {
    return subFields;
  }

  return _.reduce(
    subFields,
    (result, subField) => {
      const { key } = subField;
      const unitPlanFieldValue = getUnitPlanFieldValue({
        fieldKey,
        unitPlanFields,
      });
      const options = [];
      _.forEach(_.get(unitPlanFieldValue, `${key}`, []), option => {
        if (!_.isEmpty(option.value)) {
          options.push({ label: option.value, value: option.id });
        }
      });

      if (!_.isEmpty(options)) {
        result.push({
          ...subField,
          config: { ...subField.config, defaultOptions: options },
        });
      }

      return result;
    },
    []
  );
};
